import { CompactActionSlot } from '@components'
import { AttachmentSelector } from '@containers/AttachmentSelector'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { useAllowUpload } from '@root/hooks/useAllowUpload'
import { IAccountingSuggestion } from '@root/query/suggestions'
import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import RelationMapper from '../../../../DocumentForm/RelationMapper'
import {
  AttachmentsSubgroup,
  AttentionDot,
  Eyebrow,
  LinksAndAttachmentsZone,
  RelationsEyebrowRow,
  RelationsSubgroup,
  ReviewHintSlot,
  SuggestionContentDivider,
  UncertaintyDocHint
} from '../styles'
import { SuggestionCardFormValues } from '../types'
import { UncertaintyHoverCard } from '../uncertainty/UncertaintyHoverCard'
import { useSuggestionUncertainty } from '../uncertainty/SuggestionUncertaintyContext'

interface SuggestionCardAttachmentsSectionProps {
  suggestion: IAccountingSuggestion
  documentId: number
  attachmentIds: number[]
  onAttachmentIdsChange: (nextAttachmentIds: number[]) => void
}

export const SuggestionCardAttachmentsSection: React.FC<SuggestionCardAttachmentsSectionProps> = ({
  suggestion,
  documentId,
  attachmentIds,
  onAttachmentIdsChange
}) => {
  const { data: business } = useBusinessContext()
  const isAllowedToUpload = useAllowUpload()
  const hasEditPermission = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const hasBookkeepingFilesPermission = usePermission(PermissionSet.BOOKKEEPING_FILES)
  const hasAttachments = attachmentIds.length > 0
  const { t } = useTranslation()
  const { control } = useFormContext<SuggestionCardFormValues>()
  const postingMode = useWatch({ control, name: 'posting_mode' })
  const blueprint = useWatch({ control, name: 'blueprint' })
  const showRelationsSection =
    Boolean(business?.has_accrual_based_entries_for_invoicing) && postingMode !== 'accrual_split'
  const { highlight, isOpen } = useSuggestionUncertainty()
  const documentMissing = isOpen('missing_attachment')

  return (
    <>
      <SuggestionContentDivider />

      <LinksAndAttachmentsZone>
        {showRelationsSection && (
          <RelationsSubgroup>
            <Controller
              name="relations"
              control={control}
              render={({ field: { value, onChange } }) => (
                <RelationMapper
                  documentId={documentId}
                  value={value ?? []}
                  onChange={onChange}
                  readOnly={!hasEditPermission}
                  themeMode="light"
                  addButtonVariant="icon"
                  previewData={{
                    date: suggestion.date,
                    contactId: typeof suggestion.contact === 'number' ? suggestion.contact : null,
                    blueprint: blueprint ?? suggestion.blueprint,
                    excludeDocumentIds: [documentId]
                  }}
                  renderHeader={({ title, lead, addMenu, readOnly }) => (
                    <RelationsEyebrowRow>
                      <RelationsEyebrow $hasLead={Boolean(lead)}>{lead ?? title}</RelationsEyebrow>
                      {!readOnly && addMenu ? (
                        <CompactActionSlot>{addMenu}</CompactActionSlot>
                      ) : null}
                    </RelationsEyebrowRow>
                  )}
                />
              )}
            />
          </RelationsSubgroup>
        )}

        <AttachmentsSubgroup
          id={`suggestion-${suggestion.id}-attachments`}
          $highlight={highlight === 'attachments'}
        >
          <AttachmentsTitleRow>
            <Eyebrow>{t('document.attachments')}</Eyebrow>
            {documentMissing && (
              <AttachmentAttentionSlot $align="end">
                <AttentionDot />
                <UncertaintyHoverCard
                  code="missing_attachment"
                  align="end"
                  promptContext={{
                    contact:
                      suggestion.contact_name ||
                      t('document.suggestionCard.review.supplierFallback')
                  }}
                />
              </AttachmentAttentionSlot>
            )}
          </AttachmentsTitleRow>
          <AttachmentSelector
            value={attachmentIds}
            onChange={onAttachmentIdsChange}
            showRemoveInsteadOfViewLink={true}
            disabled={!isAllowedToUpload || !hasBookkeepingFilesPermission}
            appearance={hasAttachments ? 'dropzone' : 'section'}
            hideEmptyDescription={true}
            compactSectionEmpty={true}
          />
          {documentMissing && !hasAttachments && (
            <UncertaintyDocHint>
              {t('document.suggestionCard.review.docHintCard')}
            </UncertaintyDocHint>
          )}
        </AttachmentsSubgroup>
      </LinksAndAttachmentsZone>
    </>
  )
}

const RelationsEyebrow = styled(Eyebrow)<{ $hasLead?: boolean }>`
  font-weight: ${({ $hasLead }) => ($hasLead ? 500 : 700)};

  strong {
    font-weight: 700;
  }
`

const AttachmentsTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px ${({ theme }) => theme.spacing.sm}rem;
  flex-wrap: wrap;
`

const AttachmentAttentionSlot = styled(ReviewHintSlot)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-top: -8px;
  margin-bottom: -8px;
`
